import { Button, ButtonProps } from '@shopify/polaris'
import { useEffect, useRef } from 'react'
import useGetRedirectUrl from '@Config/fetcher/hooks/useGetRedirectUrl'
import { useCacheState } from '@Store/slices/cache'

interface GoToPrisyncProps {
  label?: string
  redirect?: string
}

// Per-instance redirect hook state so multiple buttons on the same page don't
// race each other through a shared `data` reference. The active panel id is
// read from cache.me (populated by AppConnectGuard / PanelSwitcher); we never
// embed the actual tokens in the URL — the backend issues a short-lived
// opaque code and the destination page exchanges it for tokens.
function GoToPrisync({
  label,
  redirect,
  ...props
}: GoToPrisyncProps & ButtonProps): JSX.Element {
  const [getRedirectUrl, { data, isLoading, isError, isFinished }] =
    useGetRedirectUrl()
  const { me } = useCacheState()
  const activePanelId: number | undefined = me?.id

  // Synchronously-opened placeholder window. We open it on click (so the
  // browser counts it as a user-gesture popup) and navigate it once the
  // async code-exchange fetch returns. Without this, Safari/Firefox block
  // the eventual `window.open` triggered from inside a useEffect.
  const placeholderRef = useRef<Window | null>(null)

  const handleClick = () => {
    if (!activePanelId) return
    // Open the placeholder WITHOUT noopener/noreferrer — those flags force
    // window.open to return null, which would leave us with no handle to
    // navigate the tab later (the about:blank tab then gets resolved to
    // apps.shopify.com by the Shopify admin iframe host). Tab-nabbing is
    // still prevented by nulling `opener` on the same-origin about:blank
    // placeholder before we navigate it cross-origin below.
    const win = window.open('', '_blank')
    if (win) win.opener = null
    placeholderRef.current = win
    getRedirectUrl(
      `${import.meta.env.VITE_REMOTE_APP_URL}/auth/login-with-shopify`,
      activePanelId,
    )
  }

  useEffect(() => {
    const url = (data as { url?: string } | null | undefined)?.url
    const win = placeholderRef.current
    if (!url) return
    placeholderRef.current = null
    const finalUrl = redirect
      ? `${url}&redirect=${encodeURIComponent(`/${redirect}`)}`
      : url
    if (win && !win.closed) {
      win.location.href = finalUrl
    } else {
      window.open(finalUrl, '_blank', 'noopener,noreferrer')
    }
  }, [data]) // eslint-disable-line react-hooks/exhaustive-deps

  // If the redirect fetch fails, close the placeholder tab so the merchant
  // isn't left staring at about:blank with no feedback.
  useEffect(() => {
    if (!isFinished || !isError) return
    const win = placeholderRef.current
    placeholderRef.current = null
    if (win && !win.closed) {
      win.close()
    }
  }, [isFinished, isError])

  return (
    <Button
      onClick={handleClick}
      {...props}
      // Spread `props` first so these guards always win: until `activePanelId`
      // resolves, `handleClick` early-returns, so a consumer-supplied
      // `disabled`/`loading` must not be able to re-enable the button.
      disabled={!activePanelId || props.disabled}
      loading={isLoading || !activePanelId || props.loading}
    >
      {label}
    </Button>
  )
}

export default GoToPrisync
